import { useState } from 'react'
import { Trophy } from 'lucide-react'
import { Card, CardTitle, Spinner } from '@/components/ui'
import { HelpNote, Hint } from '@/components/Hint'
import { useLeaderboard } from '@/lib/queries'
import { useAuth } from '@/context/AuthContext'
import { cn, formatNumber, formatPercent } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'
import type { LeaderboardEntry } from '@/types/api'

/** Shown before the list is expanded. Long enough to find yourself in, short
 *  enough not to be a wall of names on a phone. */
const VISIBLE = 10

/**
 * Everybody's rating, ranked, on a screen everybody can open.
 *
 * This was asked for explicitly, with the objection stated: a public ranking of
 * people is read as a verdict on the person, and a low score is often a hard
 * machine rather than a poor worker. The answer to that is not to hide the
 * list, it is to make every row accountable — so each score carries the three
 * parts it came from, the formula is printed above them, and a figure resting
 * on too few walks is marked as one.
 *
 * ## Why it is a table
 *
 * Sixty-five people against one number is a lookup. The question somebody opens
 * this with is "where am I" and then "why", and both are answered by reading a
 * row — not by a shape. A bar chart of sixty-five categories is a table drawn
 * badly.
 *
 * ## Your own row
 *
 * Marked, and pulled up beneath the list when it falls outside it. Somebody
 * ranked fortieth should not have to expand a list of sixty-five to find
 * themselves, and making them scroll past everyone above them to do it is a
 * small cruelty the screen can simply avoid.
 */
export function Leaderboard({ range }: { range: { from?: string; to?: string } }) {
  const t = useT()
  const { user } = useAuth()
  const board = useLeaderboard(range)
  const [showAll, setShowAll] = useState(false)

  if (board.isPending) return <Spinner />

  // Quietly absent rather than an error block: the home screen has real work on
  // it and a broken ranking must not get in the way of reporting a fault.
  if (board.isError || !board.data || board.data.people.length === 0) return null

  const { people, formula } = board.data
  const shown = showAll ? people : people.slice(0, VISIBLE)

  const mine = people.find((person) => person.userId === user?.id)
  const mineIsHidden = mine !== undefined && !shown.some((p) => p.userId === mine.userId)

  return (
    <Card>
      <CardTitle
        action={
          people.length > VISIBLE ? (
            <button
              type="button"
              onClick={() => setShowAll((current) => !current)}
              className="text-brand-600 min-h-11 text-sm font-medium"
            >
              {t(showAll ? 'leaderboard.showLess' : 'leaderboard.showAll', {
                count: formatNumber(people.length),
              })}
            </button>
          ) : undefined
        }
      >
        <span className="inline-flex items-center gap-2">
          <Trophy className="size-4" aria-hidden />
          {t('leaderboard.title')}
          {/* How ties rank, on the title rather than on a row: it is a fact
              about the numbering down the left-hand side, and putting it on one
              row would claim that row is the tie. */}
          <Hint>{t('leaderboard.explain.ties')}</Hint>
        </span>
      </CardTitle>

      {/*
        The formula, printed. A number people are ranked by has to be arguable,
        and it cannot be argued with unless it says what it is made of. Read
        from the server rather than written here, so the published version
        cannot drift from the one that produced the scores.
      */}
      <p className="muted mb-3 text-sm">
        {t('leaderboard.formula', {
          fiveS: formatPercent(formula.fiveS * 100),
          tasks: formatPercent(formula.followThrough * 100),
          bonus: formatNumber(formula.maxBonus),
          accepted: formatNumber(formula.acceptedForFullMarks),
        })}
      </p>

      {/*
        The three things the formula cannot say in one line, stated rather than
        hidden behind a question mark.

        A `Hint` would have been cheaper and would have been wrong: this is the
        list everybody in the plant can open and find their own name on, and the
        sentence that stops a low 5S being read as a verdict on a person has to
        be on the screen whether or not anybody goes looking for it. The long
        form of the same three rules is in the guide, one tap away.
      */}
      <HelpNote to="/uputstvo#my_numbers" className="mb-3">
        {t('leaderboard.explain.note')}
      </HelpNote>

      <ol className="space-y-1">
        {shown.map((person) => (
          <Row key={person.userId} person={person} isMe={person.userId === user?.id} />
        ))}
      </ol>

      {mineIsHidden && (
        <>
          <p className="muted my-2 text-center text-xs">···</p>
          <ol>
            <Row person={mine} isMe />
          </ol>
        </>
      )}
    </Card>
  )
}

function Row({ person, isMe }: { person: LeaderboardEntry; isMe: boolean }) {
  const t = useT()

  return (
    <li
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-2 py-2',
        // Your own row, marked by fill rather than by colour alone — the name
        // is also the reader's own, which is the strongest cue there is.
        isMe && 'bg-brand-600/10',
      )}
    >
      <span className="muted w-7 shrink-0 text-right text-sm tabular-nums">
        {person.rating.position}.
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {person.name}
          {isMe && <span className="muted ml-1.5 text-xs">{t('leaderboard.you')}</span>}
        </p>

        {/*
          The three parts, in words, on every row. This is the difference
          between a ranking somebody can act on and one they can only resent —
          "you are fortieth" answers nothing, "your 5S is 54" is a conversation.
        */}
        <p className="muted truncate text-xs">
          {person.rating.components
            .map((component) => {
              const label = t(`leaderboard.part.${component.key}` as Parameters<typeof t>[0])

              return component.value === null
                ? `${label} —`
                : `${label} ${formatPercent(component.value)}`
            })
            .join(' · ')}

          {/* The bonus, shown as an addition rather than folded into the
              total — it is the part somebody can do something about tomorrow. */}
          {person.rating.bonus > 0 &&
            ` · ${t('leaderboard.part.bonus', {
              bonus: formatNumber(person.rating.bonus),
              accepted: formatNumber(person.rating.accepted),
            })}`}
        </p>
      </div>

      {person.rating.thin && (
        // Said out loud: a rating resting on two walks reads exactly like one
        // resting on two hundred, and the ranking is what makes that matter.
        <span className="muted shrink-0 text-xs">{t('leaderboard.thin')}</span>
      )}

      <span className="shrink-0 text-lg font-semibold tabular-nums">
        {person.rating.score === null ? '—' : formatPercent(person.rating.score)}
      </span>
    </li>
  )
}
